import {
  StaticModes,
  computeStaticMode,
  computeAndSetStaticModes,
  isDynamic,
  isInherits,
  isStatic
} from "../StaticMode";
import { Color, Object3D } from "three";
import serializeColor from "../utils/serializeColor";
import LoadingCube from "../objects/LoadingCube";
import ErrorIcon from "../objects/ErrorIcon";
import traverseFilteredSubtrees from "../utils/traverseFilteredSubtrees";
import { unproxiedUrlFor } from "../../api/Api";
import { shouldDisableScalingForData } from "../utils/assetScaleUtils";

import {
  BroadActivityFeed20Regular,
  Flowchart20Regular,
  CircleImage20Regular,
  CircleHalfFill20Regular,
  HeadphonesSoundWave20Regular,
  Cloud20Regular,
  Settings20Regular,
  Cube20Regular,
  QrCode20Regular,
  Code20Regular,
  Image20Regular,
  Badge20Regular,
  AlignStraighten20Regular,
  ArrowJoin20Regular,
  WeatherPartlyCloudyDay20Regular,
  Video20Regular,
  Speaker220Regular
} from "@fluentui/react-icons";
import configs from "../../configs";
import { DEFAULT_LAYER, HELPER_LAYER } from "../Layers";

function updateObjectArrayProperty(property, value) {
  if (value) {
    const compKeys = Object.keys(value);
    compKeys.forEach(compKey => {
      const enabledProps = new Set(property[compKey]);
      const propKeys = Object.keys(value[compKey]);
      propKeys.forEach(propKey => {
        if (value[compKey][propKey] === true) {
          enabledProps.add(propKey);
        } else {
          enabledProps.delete(propKey);
        }
      });
      property[compKey] = Array.from(enabledProps);
    });
  }
}

export default function EditorNodeMixin(Object3DClass) {
  return class extends Object3DClass {
    static nodeName = "Unknown Node";

    static disableTransform = false;

    static disableVisible = false;

    static useMultiplePlacementMode = false;

    static ignoreRaycast = false;

    // Used for props like src that have side effects that we don't want to happen in the constructor
    static initialElementProps = {};

    static nodeComponentsCategories = {
      BASIC: "Basic",
      LOGIC: "Logic",
      MEDIA: "Media",
      LIGHTS: "Lights",
      COLLIDERS: "Colliders",
      ENVIRONMENT: "Environment"
    };

    static renderablePropertiesTemplate = {
      media: {
        src: { type: "text", label: "Source URL", defaultValue: undefined },
        autoplay: {
          type: "checkbox",
          label: "Autoplay",
          defaultValue: undefined
        }
      },
      items: {
        items: { type: "array", label: "Items", defaultValue: undefined },
        selectedItem: {
          type: "text",
          label: "Selected Item",
          defaultValue: undefined
        }
      },
      rendering: {
        _visible: { type: "switch", label: "Visible", defaultValue: undefined },
        _Icon: CircleImage20Regular
      },
      general: {
        enabled: { type: "switch", label: "Enabled", defaultValue: undefined },
        _Help: {
          content: "See docs",
          href: configs.editorDocs()
        },
        _Icon: BroadActivityFeed20Regular
      },
      lightSettings: {
        _Icon: Settings20Regular
      },
      model: {
        _Icon: Cube20Regular
      },
      audio: {
        _Icon: Speaker220Regular
      },
      fog: {
        _Icon: Cloud20Regular
      },
      ar: {
        _Icon: QrCode20Regular
      },
      behaviour: {
        _Icon: Code20Regular
      },
      image: {
        _Icon: Image20Regular
      },
      navMesh: {
        _Icon: AlignStraighten20Regular
      },
      groundPlane: {
        _Icon: Badge20Regular
      },
      transform: {
        position: {
          type: "position",
          label: "Position"
        },
        rotation: {
          type: "rotation",
          label: "Rotation"
        },
        scale: {
          type: "scale",
          label: "Scale"
        },
        _Icon: Flowchart20Regular
      },
      material: {
        _Icon: CircleHalfFill20Regular
      },
      audioSettings: {
        _Icon: HeadphonesSoundWave20Regular
      },
      waypoint: {
        _Icon: ArrowJoin20Regular
      },
      skybox: {
        _Icon: WeatherPartlyCloudyDay20Regular
      },
      video: {
        _Icon: Video20Regular
      }
      // Add more groups and properties as needed
    };

    static getRenderablePropertiesTemplate() {
      return this.renderablePropertiesTemplate;
    }

    getRenderablePropertiesTemplate() {
      return {
        ...this.constructor.renderablePropertiesTemplate,
        transform: {
          ...this.constructor.renderablePropertiesTemplate.transform,
          _menu: [
            {
              label: "Reset",
              action: this.reset.bind(this)
            }
          ]
        }
      };
    }

    static hideInElementsPanel = false;

    static optionalProperties = {};

    static canAddNode(_editor) {
      return true;
    }

    static async load() {
      return Promise.resolve();
    }

    static shouldDeserialize(entityJson) {
      return !!entityJson.components.find(c => c.name === this.componentName);
    }

    getScaleLockData() {
      return null;
    }

    isScaleLocked() {
      if (this.disableScaling) {
        return true;
      }

      return shouldDisableScalingForData(this.getScaleLockData());
    }

    static async deserialize(editor, json) {
      const node = new this(editor);

      node.name = json.name;

      if (json.components) {
        for (const component of json.components) {
          if (component.props && component.props.src) {
            component.props.src = unproxiedUrlFor(component.props.src);
          }
        }

        const transformComponent = json.components.find(c => c.name === "transform");

        if (transformComponent) {
          const { position, rotation, scale } = transformComponent.props;
          node.position.set(position.x, position.y, position.z);
          node.rotation.set(rotation.x, rotation.y, rotation.z);
          node.scale.set(scale.x, scale.y, scale.z);
        }

        const visibleComponent = json.components.find(c => c.name === "visible");

        if (visibleComponent) {
          node._visible = visibleComponent.props.visible;
        }

        const editorSettingsComponent = json.components.find(c => c.name === "editor-settings");

        if (editorSettingsComponent) {
          node.enabled = editorSettingsComponent.props.enabled;
          node.locked = editorSettingsComponent?.props?.locked || false;
          node._modifiedProperties =
            editorSettingsComponent.props.modifiedProperties === undefined
              ? {}
              : JSON.parse(JSON.stringify(editorSettingsComponent.props.modifiedProperties));
          node._enabledProperties =
            editorSettingsComponent.props.enabledProperties === undefined
              ? {}
              : JSON.parse(JSON.stringify(editorSettingsComponent.props.enabledProperties));
        }

        // Deserialize prefab properties from editor-settings component
        if (editorSettingsComponent && editorSettingsComponent.props) {
          node.prefabSourceId = editorSettingsComponent.props.prefabSourceId || null;
          node.isPrefabInstance = editorSettingsComponent.props.isPrefabInstance || false;
        }
      }

      return node;
    }

    constructor(editor, ...args) {
      super(...args);

      // Assign a scriptableType so ScriptableObjects can identify wrappers
      // Preference order: explicit static scriptableType on the class, componentName, nodeName, then constructor name.
      const ctor = this.constructor;
      this.scriptableType = ctor.scriptableType || ctor.componentName || ctor.nodeName || ctor.name;

      this.editor = editor;
      this.nodeName = this.constructor.nodeName;
      this.name = this.constructor.nodeName;
      this.isNode = true;
      this.disableTransform = this.constructor.disableTransform;
      this.disableCursor = this.constructor.disableCursor;
      this.disableVisible = this.constructor.disableVisible;
      this.useMultiplePlacementMode = this.constructor.useMultiplePlacementMode;
      this.ignoreRaycast = this.constructor.ignoreRaycast;

      this.staticMode = StaticModes.Inherits;
      this.originalStaticMode = null;
      this._enabled = true;
      this._visible = true;
      this.saveParent = false;
      this.loadingCube = null;
      this.errorIcon = null;
      this.placeholder = null;
      this.issues = [];
      this._modifiedProperties = {};
      this._enabledProperties = {};
      this.locked = false;
      this.helperLayer = HELPER_LAYER;
      this.disableScaling = false;

      // Prefab instance tracking (applies to all nodes)
      this.prefabSourceId = null; // Archive asset ID
      this.isPrefabInstance = false; // True for all scene assets from archive
    }

    get enabled() {
      return this._enabled;
    }

    set enabled(value) {
      //
      if (value == true) {
        this.layers.enable(DEFAULT_LAYER);
      } else {
        this.layers.disable(DEFAULT_LAYER);
      }
      this._enabled = value;
    }

    // Generic prefab helper methods (work for any node type)
    isPrefab() {
      return this.prefabSourceId !== null;
    }

    getPrefabSourceId() {
      return this.prefabSourceId;
    }

    clone(recursive) {
      return new this.constructor(this.editor).copy(this, recursive);
    }

    reset() {
      this.editor.resetTransformSelected();
    }

    copy(source, recursive = true) {
      if (recursive) {
        this.remove(this.loadingCube);
        this.remove(this.errorIcon);
        this.remove(this.placeholder);
      }

      super.copy(source, recursive);

      if (recursive) {
        const loadingCubeIndex = this.children.findIndex(child => child?.type === "LoadingCube");

        if (loadingCubeIndex !== -1) {
          this.loadingCube = this.children[loadingCubeIndex];
        }

        const errorIconIndex = source.children.findIndex(child => child === source.errorIcon);

        if (errorIconIndex !== -1) {
          this.errorIcon = this.children[errorIconIndex];
        }

        const placeholderIconIndex = source.children.findIndex(child => child === source.placeholder);

        if (placeholderIconIndex !== -1) {
          this.placeholder = this.children[placeholderIconIndex];
        }
      }

      this.issues = source.issues.slice();
      this._visible = source._visible;
      this._enabled = source.enabled;
      this.locked = source.locked;
      this._modifiedProperties =
        source._modifiedProperties === undefined ? {} : JSON.parse(JSON.stringify(source._modifiedProperties));
      this._enabledProperties =
        source._enabledProperties === undefined ? {} : JSON.parse(JSON.stringify(source._enabledProperties));

      // Copy prefab properties (maintains prefab link)
      this.prefabSourceId = source.prefabSourceId;
      this.isPrefabInstance = source.isPrefabInstance;

      return this;
    }

    get visible() {
      return this.enabled && this._visible;
    }

    set visible(value) {
      this._visible = value;
    }

    onPlay() {}

    onUpdate(dt) {
      if (this.loadingCube) {
        this.loadingCube.update(dt);
      }
    }

    onPause() {}

    onAdd() {}

    onChange() {}

    onRemove() {}

    onSelect() {}

    onDeselect() {}

    onRendererChanged() {}

    traverseEnabled(callback) {
      if (this.enabled === false) return;
      callback(this);
      const children = this.children;
      for (let i = 0, l = children.length; i < l; i++) {
        if (children[i].isNode) {
          children[i].traverseEnabled(callback);
        }
      }
    }

    addIssue(severity, message) {
      const newIssue = { severity, message };
      if (!this.issues.some(issue => issue.severity === newIssue.severity && issue.message === newIssue.message)) {
        this.issues.push(newIssue);
      }
    }

    serialize(components) {
      const entityJson = {
        name: this.name,
        components: [
          {
            name: "transform",
            props: {
              position: {
                x: this.position.x,
                y: this.position.y,
                z: this.position.z
              },
              rotation: {
                x: this.rotation.x,
                y: this.rotation.y,
                z: this.rotation.z
              },
              scale: {
                x: this.scale.x,
                y: this.scale.y,
                z: this.scale.z
              }
            }
          },
          {
            name: "visible",
            props: {
              visible: this._visible
            }
          },
          {
            name: "editor-settings",
            props: {
              locked: this.locked,
              enabled: this.enabled,
              modifiedProperties:
                this._modifiedProperties === undefined ? {} : JSON.parse(JSON.stringify(this._modifiedProperties)),
              enabledProperties:
                this._enabledProperties === undefined ? {} : JSON.parse(JSON.stringify(this._enabledProperties)),
              // Prefab properties
              prefabSourceId: this.prefabSourceId,
              isPrefabInstance: this.isPrefabInstance
            }
          }
        ]
      };

      if (components) {
        for (const componentName in components) {
          if (!Object.prototype.hasOwnProperty.call(components, componentName)) continue;

          const serializedProps = {};
          const componentProps = components[componentName];

          for (const propName in componentProps) {
            if (!Object.prototype.hasOwnProperty.call(componentProps, propName)) continue;

            const propValue = componentProps[propName];

            if (propValue instanceof Color) {
              serializedProps[propName] = serializeColor(propValue);
            } else {
              serializedProps[propName] = propValue;
            }
          }

          entityJson.components.push({
            name: componentName,
            props: serializedProps
          });
        }
      }

      return entityJson;
    }

    prepareForExport() {
      this.userData.MOZ_spoke_uuid = this.uuid;

      if (!this.visible) {
        this.addGLTFComponent("visible", {
          visible: this.visible
        });
      }
    }

    /**
     * Adds a GLTF component
     * @param {String} name The component name to be replaced
     * @param {Object} props The component properties to be set
     * @param {boolean} params Parameters object
     * @param {{THREE.Object3D} params.node The target Object3D element
     */
    addGLTFComponent(name, props) {
      if (!this.userData.gltfExtensions) {
        this.userData.gltfExtensions = {};
      }

      if (!this.userData.gltfExtensions.MOZ_hubs_components) {
        this.userData.gltfExtensions.MOZ_hubs_components = {};
      }

      if (props !== undefined && typeof props !== "object") {
        throw new Error("glTF component props must be an object or undefined");
      }

      const componentProps = {};

      for (const key in props) {
        if (!Object.prototype.hasOwnProperty.call(props, key)) continue;

        const value = props[key];

        if (value instanceof Color) {
          componentProps[key] = serializeColor(value);
        } else {
          componentProps[key] = value;
        }
      }

      this.userData.gltfExtensions.MOZ_hubs_components[name] = componentProps;
    }

    removeGLTFComponent(name) {
      if (this.userData.gltfExtensions && this.userData.gltfExtensions.MOZ_hubs_components) {
        delete this.userData.gltfExtensions.MOZ_hubs_components[name];
      }
    }

    replaceObject(replacementObject) {
      replacementObject = replacementObject || new Object3D().copy(this, false);

      replacementObject.uuid = this.uuid;

      if (this.userData.gltfExtensions && this.userData.gltfExtensions.MOZ_hubs_components) {
        replacementObject.userData.gltfExtensions.MOZ_hubs_components =
          this.userData.gltfExtensions.MOZ_hubs_components;
      }

      for (const child of this.children) {
        if (child.isNode) {
          replacementObject.children.push(child);
          child.parent = replacementObject;
        }
      }

      this.parent.add(replacementObject);
      this.parent.remove(this);
    }

    gltfIndexForUUID(nodeUUID) {
      return { __gltfIndexForUUID: nodeUUID };
    }

    getObjectByUUID(uuid) {
      return this.getObjectByProperty("uuid", uuid);
    }

    computeStaticMode() {
      return computeStaticMode(this);
    }

    computeAndSetStaticModes() {
      return computeAndSetStaticModes(this);
    }

    computeAndSetVisible() {
      this.traverse(object => {
        if (object.parent && !object.parent.visible) {
          object.visible = false;
        }
      });
    }

    showLoadingCube() {
      if (!this.loadingCube) {
        this.loadingCube = new LoadingCube();
        this.add(this.loadingCube);
      }

      const worldScale = this.getWorldScale(this.loadingCube.scale);

      if (worldScale.x === 0 || worldScale.y === 0 || worldScale.z === 0) {
        this.loadingCube.scale.set(1, 1, 1);
      } else {
        this.loadingCube.scale.set(1 / worldScale.x, 1 / worldScale.y, 1 / worldScale.z);
      }
    }

    hideLoadingCube() {
      if (this.loadingCube) {
        this.remove(this.loadingCube);
        this.loadingCube = null;
      }
    }

    showErrorIcon() {
      if (!this.errorIcon) {
        this.errorIcon = new ErrorIcon();
        this.add(this.errorIcon);
      }

      const worldScale = this.getWorldScale(this.errorIcon.scale);

      if (worldScale.x === 0 || worldScale.y === 0 || worldScale.z === 0) {
        this.errorIcon.scale.set(1, 1, 1);
      } else {
        this.errorIcon.scale.set(1 / worldScale.x, 1 / worldScale.y, 1 / worldScale.z);
      }
    }

    hideErrorIcon() {
      if (this.errorIcon) {
        this.remove(this.errorIcon);
        this.errorIcon = null;
      }
    }

    showPlaceholder(PlaceholderClass) {
      if (!this.placeholder) {
        this.placeholder = new PlaceholderClass();
        this.add(this.placeholder);
      }
    }

    hidePlaceholder() {
      if (this.placeholder) {
        this.remove(this.placeholder);
        this.placeholder = null;
      }
    }

    isInherits() {
      return isInherits(this);
    }

    isStatic() {
      return isStatic(this);
    }

    isDynamic() {
      return isDynamic(this);
    }

    findNodeByType(nodeType, includeDisabled = true) {
      let node = null;

      traverseFilteredSubtrees(this, child => {
        if (node) {
          return false;
        }

        if (!child.isNode) {
          return;
        }

        if (!child.enabled && !includeDisabled) {
          return false;
        }

        if (child.constructor === nodeType) {
          node = child;
        }
      });

      return node;
    }

    getNodesByType(nodeType, includeDisabled = true) {
      const nodes = [];

      traverseFilteredSubtrees(this, child => {
        if (!child.isNode) {
          return;
        }

        if (!child.enabled && !includeDisabled) {
          return false;
        }

        if (child.constructor === nodeType) {
          nodes.push(this);
        }
      });

      return nodes;
    }

    getNodesChildByType(nodeType, includeDisabled = true) {
      const nodes = [];

      traverseFilteredSubtrees(this, child => {
        if (!child.isNode) {
          return;
        }

        if (!child.enabled && !includeDisabled) {
          return false;
        }

        if (child.constructor === nodeType) {
          nodes.push(child);
        }
      });

      return nodes;
    }

    // Used for calculating stats for the Performance Check Dialog
    getRuntimeResourcesForStats() {
      // return { textures: [], materials: [], meshes: [], lights: [] };
    }

    // Returns the node's attribution information by default just the name
    // This should be overriding by nodes that can provide a more specific info (ie. models based on GLTF)
    getAttribution() {
      return {
        title: this.name.replace(/\.[^/.]+$/, "")
      };
    }

    // Updates attribution information. The meta information from the API is consider the most authoritative source
    // That info then would be updated with node's source information if exists.
    updateAttribution() {
      const attribution = this.getAttribution();
      this.attribution = this.attribution || {};
      if (this.meta) {
        Object.assign(
          this.attribution,
          this.meta.author
            ? {
                author: this.meta.author ? this.meta.author.replace(/ \(http.+\)/, "") : ""
              }
            : null,
          this.meta.name ? { title: this.meta.name } : this.name ? { title: this.name.replace(/\.[^/.]+$/, "") } : null,
          this.meta.author && this.meta.name && this._canonicalUrl ? { url: this._canonicalUrl } : null
        );
      }
      // Replace the attribute keys only if they don't exist otherwise
      // we give preference to the info coming from the API source over the GLTF asset
      Object.keys(this.attribution).forEach(key => {
        if (!this.attribution[key] || this.attribution[key] == null) {
          this.attribution[key] = attribution[key];
        }
      });
      // If the GLTF attribution info has keys that are missing form the API source, we add them
      for (const key in attribution) {
        if (!Object.prototype.hasOwnProperty.call(this.attribution, key)) {
          if (key === "author") {
            this.attribution[key] = attribution[key] ? attribution[key].replace(/ \(http.+\)/, "") : "";
          } else {
            this.attribution[key] = attribution[key];
          }
        }
      }
    }

    enabledPropertyExportValue(componentName, propName) {
      if (this._enabledProperties[componentName]) {
        return this._enabledProperties[componentName].includes(propName) ? this[propName] : undefined;
      }
      return undefined;
    }

    modifiedPropertyExportValue(componentName, propName) {
      if (this._modifiedProperties[componentName]) {
        return this._modifiedProperties[componentName].includes(propName) ? this[propName] : undefined;
      }
      return undefined;
    }

    exportPropertyValue(componentName, propName) {
      if (this.constructor.optionalProperties[componentName]) {
        if (this.constructor.optionalProperties[componentName].includes(propName)) {
          return this.enabledPropertyExportValue(componentName, propName);
        } else {
          return this.modifiedPropertyExportValue(componentName, propName);
        }
      } else {
        return this.modifiedPropertyExportValue(componentName, propName);
      }
    }

    set modifiedProperties(object) {
      updateObjectArrayProperty(this._modifiedProperties, object);
    }

    get modifiedProperties() {
      return this._modifiedProperties;
    }

    set enabledProperties(object) {
      updateObjectArrayProperty(this._enabledProperties, object);
    }

    get enabledProperties() {
      return this._enabledProperties;
    }
  };
}
